import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import financeApi, { FR_STATUS, REQUEST_TYPES, FINANCE_ROLES, fmtDMY, fmtDMYHM, fmtOMR } from '../utils/financeApi';
import api from '../utils/api';

// Currency options: OMR, USD, EUR first, then other common currencies.
const CURRENCIES = [
  { code: 'OMR', name: 'Omani Rial' },
  { code: 'USD', name: 'US Dollar' },
  { code: 'EUR', name: 'Euro' },
  { code: 'GBP', name: 'British Pound' },
  { code: 'AED', name: 'UAE Dirham' },
  { code: 'SAR', name: 'Saudi Riyal' },
  { code: 'KWD', name: 'Kuwaiti Dinar' },
  { code: 'BHD', name: 'Bahraini Dinar' },
  { code: 'QAR', name: 'Qatari Riyal' },
  { code: 'INR', name: 'Indian Rupee' },
  { code: 'PKR', name: 'Pakistani Rupee' },
  { code: 'EGP', name: 'Egyptian Pound' },
  { code: 'JOD', name: 'Jordanian Dinar' },
  { code: 'CNY', name: 'Chinese Yuan' },
  { code: 'JPY', name: 'Japanese Yen' },
  { code: 'CHF', name: 'Swiss Franc' },
  { code: 'CAD', name: 'Canadian Dollar' },
  { code: 'AUD', name: 'Australian Dollar' },
  { code: 'TRY', name: 'Turkish Lira' },
];

// Catches any render error in the request detail so it shows a message instead of a
// blank white page, and surfaces the error text for diagnosis.
class DetailBoundary extends React.Component {
  constructor(p) { super(p); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err, info) { console.error('[finance detail crash]', err, info); }
  render() {
    if (this.state.err) {
      return (
        <div style={{ padding: 40 }}>
          <button onClick={this.props.onBack} style={{ marginBottom: 16, padding: '8px 16px', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface2)', cursor: 'pointer' }}>← Back</button>
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: 20, color: '#991b1b' }}>
            <div style={{ fontWeight: 800, marginBottom: 8 }}>This request could not be displayed.</div>
            <div style={{ fontSize: 13 }}>Please go back and try again. If it keeps happening, share this with support: {String(this.state.err && this.state.err.message || this.state.err)}</div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const ACCENT = '#0D9488';
const inp = { width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, background: 'var(--surface)', color: 'var(--text)', boxSizing: 'border-box', fontFamily: 'inherit' };
const lbl = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--muted)', marginBottom: 5, display: 'block' };
const td = { padding: '10px 12px', fontSize: 13, color: 'var(--text)', borderBottom: '1px solid var(--border)', verticalAlign: 'top' };
const thd = { padding: '10px 12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px', color: 'var(--muted)', textAlign: 'left', borderBottom: '2px solid var(--border)' };
const btn = (bg = ACCENT, color = '#fff') => ({ padding: '8px 16px', borderRadius: 8, border: 'none', background: bg, color, fontSize: 13, fontWeight: 700, cursor: 'pointer' });

function StatusPill({ status }) {
  const s = FR_STATUS[status] || FR_STATUS.draft;
  return <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 999, background: s.bg, color: s.color, whiteSpace: 'nowrap' }}>{s.label}</span>;
}

function Modal({ title, onClose, children, width = 620 }) {
  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
      <div style={{ background: 'var(--surface)', borderRadius: 14, width, maxWidth: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 22px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: 'var(--text)' }}>{title}</h3>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 22, color: 'var(--muted)', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: 22, overflowY: 'auto' }}>{children}</div>
      </div>
    </div>
  );
}

export default function FinanceRequestsPage() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState('requests'); // requests | dashboard | admin
  const [requests, setRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [newType, setNewType] = useState(null); // 'iou' | 'expense'
  const [toast, setToast] = useState('');
  const [profile, setProfile] = useState(null);
  const [filters, setFilters] = useState({ year: '', month: '', status: '', type: '' });
  const showToast = (m) => { setToast(m); setTimeout(() => setToast(''), 3500); };

  const loadAll = useCallback(() => {
    const qs = [];
    if (filters.year) qs.push(`year=${filters.year}`);
    if (filters.month) qs.push(`month=${filters.month}`);
    financeApi.get('/requests' + (qs.length ? '?' + qs.join('&') : '')).then(setRequests).catch(() => setRequests([]));
  }, [filters.year, filters.month]);
  const loadProfile = useCallback(() => {
    financeApi.get('/me').catch(() => null); // warm
    // Shared profile (name/title/department/signature) from /api/profile.
    fetch('/api/profile', { headers: { Authorization: `Bearer ${localStorage.getItem('flowup_token')}` }, credentials: 'include' })
      .then(r => r.ok ? r.json() : null).then(setProfile).catch(() => setProfile(null));
  }, []);
  const refreshMe = useCallback(() => { financeApi.get('/me').then(setMe).catch(() => {}); }, []);
  useEffect(() => {
    financeApi.get('/me').then(setMe).catch(() => setMe(null));
    financeApi.get('/users').then(setUsers).catch(() => setUsers([]));
    loadProfile();
  }, [loadProfile]);
  useEffect(() => { loadAll(); }, [loadAll]);
  // Silent live refresh: keep the list and the approvals badge current without a
  // browser reload. Re-fetches quietly on an interval and when the tab regains focus.
  // IMPORTANT: do NOT refresh while a form or a request detail is open, or the parent
  // re-render kicks the user out of the field they are typing in.
  useEffect(() => {
    const busy = () => !!newType || !!activeId;
    const tick = () => { if (busy()) return; loadAll(); refreshMe(); };
    const iv = setInterval(tick, 12000);
    const onFocus = () => tick();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(iv); window.removeEventListener('focus', onFocus); };
  }, [loadAll, refreshMe, newType, activeId]);

  if (activeId) return <DetailBoundary onBack={() => { setActiveId(null); loadAll(); refreshMe(); }}><RequestDetail id={activeId} me={me} users={users} onBack={() => { setActiveId(null); loadAll(); refreshMe(); }} onChanged={() => { loadAll(); refreshMe(); }} showToast={showToast} /></DetailBoundary>;

  const navItems = [
    { key: 'requests', label: me?.is_manager ? 'All Requests' : 'My Requests', icon: '📄' },
    ...((me?.is_approver || me?.is_manager) ? [{ key: 'approvals', label: 'Approvals', icon: '✓', badge: me?.pending_approvals }] : []),
    { key: 'dashboard', label: 'Dashboard', icon: '📊' },
    ...(me?.is_manager ? [{ key: 'admin', label: 'Roles & Access', icon: '🔑' }] : []),
    ...(me?.is_manager ? [{ key: 'settings', label: 'Notifications & Email', icon: '🔔' }] : []),
  ];

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex' }}>
      {/* Sidebar */}
      <aside style={{ width: 240, background: 'var(--surface)', borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', position: 'sticky', top: 0, height: '100vh' }}>
        <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--border)' }}>
          <button onClick={() => navigate('/hub')} style={{ ...btn('var(--surface2)', 'var(--text)'), padding: '6px 12px', fontSize: 12, marginBottom: 12 }}>← Hub</button>
          <div style={{ fontSize: 16, fontWeight: 850, color: 'var(--text)' }}>Finance Requests</div>
          <div style={{ fontSize: 11, color: 'var(--muted2)', marginTop: 2 }}>{me ? roleName(me.finance_role) : ''}</div>
        </div>
        <div style={{ padding: '16px 16px 8px' }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--muted)', marginBottom: 8, paddingLeft: 4 }}>New Request</div>
          <button onClick={() => setNewType('iou')} style={{ ...btn(), width: '100%', marginBottom: 8, textAlign: 'left' }}>＋ IOU / Cash Advance</button>
          <button onClick={() => setNewType('expense')} style={{ ...btn('#4F46E5'), width: '100%', marginBottom: 8, textAlign: 'left' }}>＋ Expense Reimbursement</button>
          <button onClick={() => setNewType('creditcard')} style={{ ...btn('#B45309'), width: '100%', textAlign: 'left' }}>＋ Corporate Credit Card</button>
        </div>
        <nav style={{ padding: '14px 12px', display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--muted)', margin: '4px 0 8px', paddingLeft: 8 }}>Menu</div>
          {navItems.map(it => (
            <button key={it.key} onClick={() => setTab(it.key)}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13.5, fontWeight: 700, textAlign: 'left',
                background: tab === it.key ? 'rgba(13,148,136,0.12)' : 'transparent', color: tab === it.key ? ACCENT : 'var(--muted2)' }}>
              <span>{it.icon}</span><span style={{ flex: 1 }}>{it.label}</span>
              {it.badge ? <span style={{ background: '#DC2626', color: '#fff', fontSize: 10, fontWeight: 800, borderRadius: 999, padding: '1px 7px' }}>{it.badge}</span> : null}
            </button>
          ))}
        </nav>
      </aside>

      <main style={{ flex: 1, padding: 28, overflowY: 'auto', height: '100vh' }}>
        <div style={{ maxWidth: 'min(96vw, 1760px)', margin: '0 auto' }}>
          {tab === 'requests' && (
            <>
              <h2 style={{ margin: '0 0 16px', fontSize: 20, fontWeight: 850, color: 'var(--text)' }}>{me?.is_manager ? 'All Requests' : 'My Requests'}</h2>
              <Filters filters={filters} setFilters={setFilters} />
              <RequestsTable requests={applyClientFilters(requests, filters)} me={me} onOpen={setActiveId} />
            </>
          )}
          {tab === 'approvals' && <ApprovalsTable onOpen={setActiveId} />}
          {tab === 'dashboard' && <Dashboard me={me} />}
          {tab === 'admin' && me?.is_manager && <AdminPanel users={users} onChanged={() => financeApi.get('/users').then(setUsers)} showToast={showToast} />}
          {tab === 'settings' && me?.is_manager && <SettingsPanel showToast={showToast} />}
        </div>
      </main>

      {newType && <RequestForm type={newType} me={me} users={users} profile={profile} onClose={() => setNewType(null)} onSaved={(id) => { setNewType(null); loadAll(); setActiveId(id); }} showToast={showToast} />}
      {toast && <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: '#111827', color: '#fff', padding: '10px 18px', borderRadius: 8, fontSize: 13, zIndex: 2000 }}>{toast}</div>}
    </div>
  );
}

function applyClientFilters(rows, f) {
  return rows.filter(r => (!f.status || r.status === f.status) && (!f.type || r.request_type === f.type));
}

function Filters({ filters, setFilters }) {
  const years = []; const ny = new Date().getFullYear();
  for (let y = ny; y >= ny - 5; y--) years.push(y);
  const months = ['', 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  const monthName = (m) => ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m];
  const sel = { padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, background: 'var(--surface)', color: 'var(--text)' };
  const set = (k, v) => setFilters(p => ({ ...p, [k]: v }));
  return (
    <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
      <select style={sel} value={filters.year} onChange={e => set('year', e.target.value)}>
        <option value="">All years</option>{years.map(y => <option key={y} value={y}>{y}</option>)}
      </select>
      <select style={sel} value={filters.month} onChange={e => set('month', e.target.value)}>
        {months.map(m => <option key={m} value={m}>{m === '' ? 'All months' : monthName(m)}</option>)}
      </select>
      <select style={sel} value={filters.type} onChange={e => set('type', e.target.value)}>
        <option value="">All types</option><option value="iou">I Owe You</option><option value="expense">Expense</option>
      </select>
      <select style={sel} value={filters.status} onChange={e => set('status', e.target.value)}>
        <option value="">All statuses</option>
        {Object.keys(FR_STATUS).map(s => <option key={s} value={s}>{FR_STATUS[s].label}</option>)}
      </select>
      {(filters.year || filters.month || filters.type || filters.status) &&
        <button onClick={() => setFilters({ year: '', month: '', status: '', type: '' })} style={{ ...sel, cursor: 'pointer', color: ACCENT, fontWeight: 700 }}>Clear</button>}
    </div>
  );
}

function ApprovalsTable({ onOpen }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => financeApi.get('/approvals').then(setRows).catch(() => setRows([])), []);
  useEffect(() => {
    load();
    const iv = setInterval(load, 12000);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(iv); window.removeEventListener('focus', onFocus); };
  }, [load]);
  return (
    <>
      <h2 style={{ margin: '0 0 6px', fontSize: 20, fontWeight: 850 }}>Approvals</h2>
      <p style={{ margin: '0 0 18px', fontSize: 13, color: 'var(--muted2)' }}>Requests awaiting your decision.</p>
      {rows.length === 0 ? <div style={{ color: 'var(--muted)', padding: 40, textAlign: 'center', background: 'var(--surface)', borderRadius: 12, border: '1px solid var(--border)' }}>Nothing awaiting your approval.</div>
        : <RequestsTable requests={rows} me={{}} onOpen={onOpen} />}
    </>
  );
}

function RequestsTable({ requests, me, onOpen }) {
  if (!requests.length) return <div style={{ color: 'var(--muted)', padding: 40, textAlign: 'center', background: 'var(--surface)', borderRadius: 12, border: '1px solid var(--border)' }}>No requests yet. Create one above.</div>;
  return (
    <div style={{ background: 'var(--surface)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>
          <th style={thd}>Number</th><th style={thd}>Type</th><th style={thd}>Requester</th>
          <th style={thd}>Amount</th><th style={thd}>Status</th><th style={thd}>Date</th>
        </tr></thead>
        <tbody>
          {requests.map(r => (
            <tr key={r.id} onClick={() => onOpen(r.id)} style={{ cursor: 'pointer' }}>
              <td style={{ ...td, fontWeight: 700 }}>{r.request_number}</td>
              <td style={td}>{REQUEST_TYPES[r.request_type]?.short || r.request_type}</td>
              <td style={td}>{r.requester_name}</td>
              <td style={td}>{fmtOMR(r.total_amount)} {r.currency}</td>
              <td style={td}><StatusPill status={r.status} /></td>
              <td style={td}>{fmtDMY(r.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Create / edit request form ──────────────────────────────────────────────
function RequestForm({ type, me, users, profile, request, onClose, onSaved, showToast }) {
  const isEdit = !!request;
  const [f, setF] = useState({
    requester_name: request?.requester_name || profile?.full_name || me?.full_name || '',
    job_title: request?.job_title || profile?.job_title || '',
    department: request?.department || profile?.department || '',
    purpose: request?.purpose || '',
    description: request?.description || '',
    currency: request?.currency || 'OMR',
    vendor: request?.vendor || '',
    cost_center: request?.cost_center || '',
    justification: request?.justification || '',
    app_note: request?.app_note || '',
  });
  const [items, setItems] = useState(request?.items?.length ? request.items.map(i => ({ item_date: i.item_date || '', description: i.description || '', amount: i.amount || '' })) : [{ item_date: '', description: '', amount: '' }]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const setItem = (idx, k, v) => setItems(its => its.map((it, i) => i === idx ? { ...it, [k]: v } : it));
  const total = items.reduce((s, it) => s + (Number(it.amount) || 0), 0);
  const isCC = type === 'creditcard';
  const isIOU = type === 'iou';
  const needsPD = isCC || isIOU; // their documents print Purpose + Description
  const [errors, setErrors] = useState({});

  // Which fields are required for THIS type (drives the asterisk markers).
  const requiredFields = ['requester_name', 'job_title', 'department', ...(needsPD ? ['purpose', 'description'] : []), ...(isCC ? ['vendor', 'justification'] : [])];
  const errStyle = (k) => errors[k] ? { border: '1.5px solid #DC2626', background: 'rgba(220,38,38,0.04)' } : {};

  const save = async () => {
    if (!String(f.requester_name || '').trim()) { setErrors({ requester_name: true }); showToast('Enter the requester name to save.'); return; }
    setSaving(true);
    try {
      const payload = { request_type: type, ...f, estimate_amount: total, items: items.filter(it => it.description || it.amount) };
      let r;
      if (isEdit) r = await financeApi.put(`/requests/${request.id}`, payload);
      else r = await financeApi.post('/requests', payload);
      onSaved(r.id);
    } catch (e2) { showToast(e2.error || 'Failed'); }
    setSaving(false);
  };

  // Label with a required asterisk.
  const rl = (text, key) => <label style={lbl}>{text}{requiredFields.includes(key) || key === 'items' ? <span style={{ color: '#DC2626', marginLeft: 3 }}>*</span> : null}</label>;

  const titleText = isEdit ? `Edit ${request.request_number}` : `New ${REQUEST_TYPES[type]?.label || 'Request'}`;
  return (
    <Modal title={titleText} onClose={onClose} width={720}>
      <div style={{ display: 'flex', gap: 14, marginBottom: 14 }}>
        <div style={{ flex: 1 }}>{rl('Employee / Claimant Name', 'requester_name')}<input style={{ ...inp, ...errStyle('requester_name') }} value={f.requester_name} onChange={e => set('requester_name', e.target.value)} /></div>
        <div style={{ flex: 1 }}>{rl('Job Title', 'job_title')}<input style={{ ...inp, ...errStyle('job_title') }} value={f.job_title} onChange={e => set('job_title', e.target.value)} /></div>
      </div>
      <div style={{ display: 'flex', gap: 14, marginBottom: 14 }}>
        <div style={{ flex: 1 }}>{rl('Department', 'department')}<input style={{ ...inp, ...errStyle('department') }} value={f.department} onChange={e => set('department', e.target.value)} /></div>
        <div style={{ width: 150 }}><label style={lbl}>Currency</label>
          <select style={inp} value={f.currency} onChange={e => set('currency', e.target.value)}>
            {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
          </select>
        </div>
      </div>

      {isCC && (
        <div style={{ marginBottom: 14 }}>
          {rl('Vendor / Supplier', 'vendor')}<input style={{ ...inp, ...errStyle('vendor') }} value={f.vendor} onChange={e => set('vendor', e.target.value)} />
        </div>
      )}

      {/* Purpose/Description appear on the IOU and Credit Card documents, so those forms ask
          for them. The Expense Claim document has none: its line items carry the detail. */}
      {needsPD && <div style={{ marginBottom: 14 }}>{rl('Purpose', 'purpose')}<textarea rows={2} style={{ ...inp, minHeight: 44, resize: 'vertical', fontFamily: 'inherit', ...errStyle('purpose') }} value={f.purpose} onChange={e => set('purpose', e.target.value)} placeholder="Purpose of this request" /></div>}

      {needsPD && <div style={{ marginBottom: 14 }}>{rl('Description', 'description')}<textarea rows={3} style={{ ...inp, minHeight: 60, resize: 'vertical', fontFamily: 'inherit', ...errStyle('description') }} value={f.description || ''} onChange={e => set('description', e.target.value)} placeholder="Detailed description of this request" /></div>}

      {isCC && (
        <div style={{ marginBottom: 14 }}>{rl('Business Justification', 'justification')}<textarea rows={2} style={{ ...inp, minHeight: 44, resize: 'vertical', fontFamily: 'inherit', ...errStyle('justification') }} value={f.justification} onChange={e => set('justification', e.target.value)} placeholder="Why this purchase is needed" /></div>
      )}

      {rl(type === 'expense' ? 'Expenses' : isCC ? 'Estimated Items' : 'Cash Requested', 'items')}
      <div style={{ border: errors.items ? '1.5px solid #DC2626' : '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', marginBottom: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            {type === 'expense' && <th style={{ ...thd, padding: '8px 10px' }}>Date</th>}
            <th style={{ ...thd, padding: '8px 10px' }}>Description &amp; Purpose</th>
            <th style={{ ...thd, padding: '8px 10px', width: 130 }}>{isCC ? 'Est. Amount' : 'Amount'}</th>
            <th style={{ ...thd, padding: '8px 10px', width: 40 }}></th>
          </tr></thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i}>
                {type === 'expense' && <td style={{ ...td, padding: 6 }}><input type="date" style={{ ...inp, padding: '6px 8px' }} value={it.item_date} onChange={e => setItem(i, 'item_date', e.target.value)} /></td>}
                <td style={{ ...td, padding: 6 }}><textarea rows={2} style={{ ...inp, padding: '6px 8px', minHeight: 38, resize: 'vertical', fontFamily: 'inherit' }} value={it.description} onChange={e => setItem(i, 'description', e.target.value)} placeholder="Description (multiple lines allowed)" /></td>
                <td style={{ ...td, padding: 6 }}><input type="number" step="0.001" style={{ ...inp, padding: '6px 8px' }} value={it.amount} onChange={e => setItem(i, 'amount', e.target.value)} /></td>
                <td style={{ ...td, padding: 6, textAlign: 'center' }}>{items.length > 1 && <button onClick={() => setItems(items.filter((_, j) => j !== i))} style={{ border: 'none', background: 'none', color: '#DC2626', cursor: 'pointer', fontSize: 16 }}>×</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
        <button onClick={() => setItems([...items, { item_date: '', description: '', amount: '' }])} style={{ ...btn('var(--surface2)', ACCENT), padding: '6px 12px', fontSize: 12 }}>+ Add line</button>
        <div style={{ fontSize: 14, fontWeight: 800 }}>{isCC ? 'Estimate' : 'Total'}: {fmtOMR(total)} {f.currency}</div>
      </div>

      {/* App-only note: visible in the app, never printed on the PDF/Word document. */}
      <div style={{ marginBottom: 14 }}>
        <label style={lbl}>Note <span style={{ color: 'var(--muted2)', fontWeight: 400 }}>(optional, shown in the app only, not on the document)</span></label>
        <textarea rows={2} style={{ ...inp, minHeight: 44, resize: 'vertical', fontFamily: 'inherit' }} value={f.app_note || ''} onChange={e => set('app_note', e.target.value)} placeholder="Internal note for reference (not printed)" />
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--muted2)', marginBottom: 14, padding: '8px 12px', background: 'var(--surface2)', borderRadius: 8 }}>
        {type === 'expense' && 'All fields are required. You must attach the invoice / receipt before submitting.'}
        {type === 'iou' && 'All fields are required. Evidence is optional now; after spending you will submit a settlement with receipts.'}
        {isCC && 'All fields are required. Attach a pre-approval document (quotation, vendor proposal, purchase request) before submitting. After use you will submit a settlement with evidence.'}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, borderTop: '1px solid var(--border)', paddingTop: 16 }}>
        <span style={{ fontSize: 11.5, color: 'var(--muted2)' }}><span style={{ color: '#DC2626' }}>*</span> Required to submit</span>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={btn('var(--surface2)', 'var(--text)')}>Cancel</button>
          <button onClick={save} disabled={saving} style={btn()}>{saving ? 'Saving…' : 'Save Draft'}</button>
        </div>
      </div>
    </Modal>
  );
}

// ── Request detail (workflow + attachments + history) ────────────────────────
function RequestDetail({ id, me, users, onBack, onChanged, showToast }) {
  const [r, setR] = useState(null);
  const [tab, setTab] = useState('details'); // details | history
  const [editing, setEditing] = useState(false);
  const [managerId, setManagerId] = useState('');
  const [decisionComment, setDecisionComment] = useState('');
  const [advanceAmount, setAdvanceAmount] = useState('');
  const [advanceRef, setAdvanceRef] = useState('');
  const [spent, setSpent] = useState('');
  const [settleNote, setSettleNote] = useState('');
  const [settleManagerId, setSettleManagerId] = useState('');
  const [busy, setBusy] = useState(false);
  const [missingFields, setMissingFields] = useState(null);
  const [confirmCfg, setConfirmCfg] = useState(null); // { title, message, danger, onYes }
  const [sigGate, setSigGate] = useState(null); // { onDone } — signature-required gate before approving

  const load = useCallback(() => { financeApi.get(`/requests/${id}`).then(setR).catch(() => setR(null)); }, [id]);
  useEffect(() => { load(); }, [load]);
  if (!r) return <div style={{ padding: 40 }}>Loading…</div>;

  const twoPhase = r.request_type === 'iou' || r.request_type === 'creditcard';
  const isOwner = r.user_id === me?.id;
  const isAssignedApprover = r.manager_id === me?.id;
  const canEdit = (isOwner && ['draft', 'manager_rejected', 'finance_rejected'].includes(r.status)) || (me?.is_manager && ['draft', 'manager_rejected', 'finance_rejected'].includes(r.status));
  const canSubmit = (isOwner || me?.is_manager) && ['draft', 'manager_rejected', 'finance_rejected'].includes(r.status);
  const canManagerDecide = (me?.is_manager || isAssignedApprover) && r.status === 'pending_manager';
  const canFinanceDecide = me?.is_manager && r.status === 'pending_finance';
  const canDelete = me?.is_manager || (isOwner && r.status === 'draft');
  // Settlement-phase capabilities.
  const canSubmitSettlement = isOwner && r.status === 'awaiting_settlement';
  const canApproveSettlement = r.status === 'settlement_manager' && (me?.is_manager || r.settlement_manager_id === me?.id);
  const canFinanceConfirm = me?.is_manager && r.status === 'settlement_review';
  const canUserConfirm = isOwner && r.status === 'settlement_confirm';
  const inSettlement = ['awaiting_settlement', 'settlement_review', 'settlement_confirm'].includes(r.status);

  const act = async (fn) => { setBusy(true); try { await fn(); load(); if (onChanged) onChanged(); } catch (e) { showToast(e.error || 'Failed'); } setBusy(false); };

  // Before an APPROVAL, the approver must have a signature on file. If not, open a
  // polite upload window; once uploaded (saved to their profile for reuse across the
  // platform), the original approval action runs.
  const requireSignatureThen = async (proceed) => {
    try {
      const prof = (await api.get('/profile')).data;
      if (prof && prof.has_signature) { proceed(); return; }
    } catch (e) { /* if the check fails, fall through to the prompt */ }
    setSigGate({ onDone: () => { setSigGate(null); proceed(); } });
  };
  const submit = async () => {
    if (!managerId) { showToast(r.request_type === 'creditcard' ? 'Choose a Card Holder.' : 'Choose an approver.'); return; }
    setMissingFields(null);
    setBusy(true);
    try {
      await financeApi.post(`/requests/${id}/submit`, { manager_id: managerId });
      load(); if (onChanged) onChanged();
    } catch (e) {
      if (e.missing && e.missing.length) setMissingFields(e.missing);
      else showToast(e.error || 'Failed');
    }
    setBusy(false);
  };
  const managerDecision = (decision) => act(() => financeApi.post(`/requests/${id}/manager-decision`, { decision, comment: decisionComment }).then(() => setDecisionComment('')));
  const financeDecision = (decision) => act(() => financeApi.post(`/requests/${id}/finance-decision`, { decision, comment: decisionComment, advance_amount: advanceAmount !== '' ? Number(advanceAmount) : undefined, advance_ref: advanceRef || undefined }).then(() => { setDecisionComment(''); setAdvanceAmount(''); setAdvanceRef(''); }));
  const submitSettlement = () => {
    if (spent === '') { showToast('Enter the amount spent.'); return; }
    if (r.request_type === 'iou' && !settleManagerId) { showToast('Choose a Line Manager to approve the settlement.'); return; }
    act(() => financeApi.post(`/requests/${id}/settlement`, { spent: Number(spent), note: settleNote, settlement_manager_id: settleManagerId || undefined }).then(() => { setSpent(''); setSettleNote(''); setSettleManagerId(''); }));
  };
  const settlementManagerDecision = (decision) => act(() => financeApi.post(`/requests/${id}/settlement-manager`, { decision, comment: decisionComment }).then(() => setDecisionComment('')));
  const settleFinance = (decision) => act(() => financeApi.post(`/requests/${id}/settlement-finance`, { decision, comment: decisionComment }).then(() => setDecisionComment('')));
  const settleConfirm = () => act(() => financeApi.post(`/requests/${id}/settlement-confirm`, {}));

  const uploadFiles = async (e, phase) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    const fd = new FormData(); files.forEach(f => fd.append('files', f));
    if (phase) fd.append('phase', phase);
    setBusy(true);
    try { await financeApi.post(`/requests/${id}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); load(); }
    catch (err) { showToast(err.error || 'Upload failed'); }
    setBusy(false);
  };
  const reqAttachments = (r.attachments || []).filter(a => (a.phase || 'request') === 'request');
  const settlementAttachments = (r.attachments || []).filter(a => a.phase === 'settlement');
  const token = localStorage.getItem('flowup_token');
  const pdfUrl = `/api/finance-requests/requests/${id}/pdf?token=${token}`;
  const wordUrl = `/api/finance-requests/requests/${id}/word?token=${token}`;

  return (
    <div style={{ height: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ flexShrink: 0, background: 'var(--surface)', borderBottom: '1px solid var(--border)', padding: '14px 28px', display: 'flex', alignItems: 'center', gap: 16 }}>
        <button onClick={onBack} style={{ ...btn('var(--surface2)', 'var(--text)'), padding: '6px 12px' }}>← Back</button>
        <div>
          <div style={{ fontSize: 17, fontWeight: 850, color: 'var(--text)' }}>{r.request_number}</div>
          <div style={{ fontSize: 12, color: 'var(--muted2)' }}>{REQUEST_TYPES[r.request_type]?.label || r.request_type} · {r.requester_name}</div>
        </div>
        <div style={{ flex: 1 }} />
        <StatusPill status={r.status} />
        <a href={wordUrl} style={{ ...btn('var(--surface2)', '#2563EB'), textDecoration: 'none' }}>⬇ Word</a>
        <a href={pdfUrl} target="_blank" rel="noreferrer" style={{ ...btn('var(--surface2)', ACCENT), textDecoration: 'none' }}>⬇ PDF Package</a>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        <div style={{ maxWidth: 'min(96vw, 1760px)', margin: '0 auto', padding: '24px clamp(20px, 3vw, 48px) 60px' }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 20, background: 'var(--surface2)', borderRadius: 10, padding: 4, width: 'fit-content' }}>
            {['details', 'history'].map(t => (
              <button key={t} onClick={() => setTab(t)} style={{ padding: '7px 18px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700, textTransform: 'capitalize', background: tab === t ? ACCENT : 'transparent', color: tab === t ? '#fff' : 'var(--muted2)' }}>{t}</button>
            ))}
          </div>

        {tab === 'details' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) clamp(320px, 24vw, 420px)', gap: 24, alignItems: 'start' }}>
            {/* LEFT: request body */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20, minWidth: 0 }}>
              {/* Requester summary */}
              <div style={{ background: 'var(--surface)', borderRadius: 12, border: '1px solid var(--border)', padding: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 14 }}>Request Details</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
                  {[['Requester', r.requester_name], ['Job Title', r.job_title], ['Department', r.department], ['Currency', r.currency],
                    ...(r.request_type === 'creditcard' ? [['Vendor / Supplier', r.vendor], ['Estimated Amount', r.estimate_amount != null ? `${fmtOMR(r.estimate_amount)} ${r.currency}` : '—']] : []),
                    ...(r.request_type === 'creditcard' ? [['Purpose', r.purpose], ['Business Justification', r.justification]] : []),
                    ...(r.request_type === 'iou' ? [['Purpose', r.purpose], ['Description', r.description]] : []),
                    ...(r.app_note ? [['Note (app only)', r.app_note]] : [])].map(([k, v]) => (
                    <div key={k} style={(k === 'Purpose' || k === 'Description' || k === 'Business Justification' || k.startsWith('Note')) ? { gridColumn: '1 / -1' } : {}}>
                      <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px', color: 'var(--muted)' }}>{k}</div>
                      <div style={{ fontSize: 13.5, color: 'var(--text)', marginTop: 3, whiteSpace: 'pre-wrap' }}>{v || '—'}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Items */}
              <div style={{ background: 'var(--surface)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>
                    {r.request_type === 'expense' && <th style={thd}>Date</th>}
                    <th style={thd}>Description &amp; Purpose</th><th style={{ ...thd, textAlign: 'right' }}>Amount</th>
                  </tr></thead>
                  <tbody>
                    {r.items.map(it => (
                      <tr key={it.id}>
                        {r.request_type === 'expense' && <td style={td}>{it.item_date || '—'}</td>}
                        <td style={{ ...td, whiteSpace: 'pre-wrap' }}>{it.description}</td>
                        <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtOMR(it.amount)} {r.currency}</td>
                      </tr>
                    ))}
                    <tr><td style={{ ...td, fontWeight: 800 }} colSpan={r.request_type === 'expense' ? 2 : 1}>Total</td><td style={{ ...td, fontWeight: 800, textAlign: 'right' }}>{fmtOMR(r.total_amount)} {r.currency}</td></tr>
                  </tbody>
                </table>
              </div>

              {/* Attachments — request evidence */}
              <div style={{ background: 'var(--surface)', borderRadius: 12, border: '1px solid var(--border)', padding: 18 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 800 }}>{twoPhase ? 'Request Attachments' : 'Attachments'} ({reqAttachments.length})</div>
                  {canEdit && <label style={{ ...btn('var(--surface2)', ACCENT), cursor: 'pointer', fontSize: 12 }}>+ Upload (PDF/PNG/JPG)<input type="file" multiple accept=".pdf,.png,.jpg,.jpeg" onChange={(e) => uploadFiles(e, 'request')} style={{ display: 'none' }} /></label>}
                </div>
                {reqAttachments.length === 0 ? <div style={{ fontSize: 12, color: 'var(--muted)' }}>No supporting documents.</div> :
                  reqAttachments.map(a => (
                    <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                      <span style={{ flex: 1, fontSize: 13 }}>📎 {a.file_name}</span>
                      <a href={`/api/finance-requests/attachments/${a.id}/download?token=${token}`} style={{ fontSize: 12, color: ACCENT, textDecoration: 'none' }}>Download</a>
                      {canEdit && <button onClick={() => act(() => financeApi.delete(`/attachments/${a.id}`))} title="Remove attachment" style={{ border: '1px solid var(--border)', background: 'var(--surface2)', color: '#DC2626', cursor: 'pointer', borderRadius: 7, padding: '5px 12px', fontSize: 12, fontWeight: 700 }}>Remove</button>}
                    </div>
                  ))}
              </div>

              {/* Settlement evidence (two-phase only, once in settlement) */}
              {twoPhase && (inSettlement || r.status === 'completed') && (
                <div style={{ background: 'var(--surface)', borderRadius: 12, border: '1px solid var(--border)', padding: 18 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 800 }}>Settlement Evidence ({settlementAttachments.length})</div>
                    {canSubmitSettlement && <label style={{ ...btn('var(--surface2)', ACCENT), cursor: 'pointer', fontSize: 12 }}>+ Upload evidence<input type="file" multiple accept=".pdf,.png,.jpg,.jpeg" onChange={(e) => uploadFiles(e, 'settlement')} style={{ display: 'none' }} /></label>}
                  </div>
                  {settlementAttachments.length === 0 ? <div style={{ fontSize: 12, color: 'var(--muted)' }}>No settlement evidence yet (receipts, invoices, POS slips…).</div> :
                    settlementAttachments.map(a => (
                      <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                        <span style={{ flex: 1, fontSize: 13 }}>🧾 {a.file_name}</span>
                        <a href={`/api/finance-requests/attachments/${a.id}/download?token=${token}`} style={{ fontSize: 12, color: ACCENT, textDecoration: 'none' }}>Download</a>
                        {canSubmitSettlement && <button onClick={() => act(() => financeApi.delete(`/attachments/${a.id}`))} title="Remove attachment" style={{ border: '1px solid var(--border)', background: 'var(--surface2)', color: '#DC2626', cursor: 'pointer', borderRadius: 7, padding: '5px 12px', fontSize: 12, fontWeight: 700 }}>Remove</button>}
                      </div>
                    ))}
                </div>
              )}
            </div>

            {/* RIGHT: status + workflow + key dates */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20, position: 'sticky', top: 0 }}>
              <div style={{ background: 'var(--surface)', borderRadius: 12, border: '1px solid var(--border)', padding: 18 }}>
                <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 14 }}>Status & Approvals</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                  <StatusPill status={r.status} />
                </div>
                {(() => {
                  // "Currently with" — the named person the request is waiting on right now.
                  let withWho = null, withRole = null;
                  if (r.status === 'pending_manager') { withWho = r.manager_name; withRole = 'Division Manager'; }
                  else if (r.status === 'pending_finance') { withWho = r.finance_name || 'Finance'; withRole = 'Finance Manager'; }
                  else if (r.status === 'settlement_manager') { withWho = r.settlement_manager_name || r.manager_name; withRole = 'Division Manager (settlement)'; }
                  else if (r.status === 'settlement_review' || r.status === 'settlement_confirm') { withWho = r.finance_name || 'Finance'; withRole = 'Finance (settlement)'; }
                  if (!withWho) return null;
                  return (
                    <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, padding: '9px 12px', marginBottom: 14 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#1D4ED8', textTransform: 'uppercase', letterSpacing: .4 }}>Currently with</div>
                      <div style={{ fontSize: 13.5, fontWeight: 700, color: '#1E3A8A', marginTop: 2 }}>{withWho}</div>
                      <div style={{ fontSize: 11.5, color: '#3B5BA5' }}>{withRole} — awaiting approval</div>
                    </div>
                  );
                })()}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {[['Created', fmtDMYHM(r.created_at)],
                    ['Submitted', r.submitted_at ? fmtDMYHM(r.submitted_at) : '—'],
                    [r.request_type === 'creditcard' ? 'Card Holder' : 'Approver', r.manager_name || '—'],
                    ['Reviewed', r.manager_decision_at ? fmtDMYHM(r.manager_decision_at) : '—'],
                    ['Finance', r.finance_name || '—'],
                    ['Completed', r.completed_at ? fmtDMYHM(r.completed_at) : '—']].map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5 }}>
                      <span style={{ color: 'var(--muted)' }}>{k}</span>
                      <span style={{ color: 'var(--text)', fontWeight: 600, textAlign: 'right', maxWidth: 180 }}>{v}</span>
                    </div>
                  ))}
                </div>
                {twoPhase && (inSettlement || r.status === 'completed') && (
                  <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {r.request_type === 'iou' && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5 }}>
                        <span style={{ color: 'var(--muted)' }}>Advance Paid</span>
                        <span style={{ fontWeight: 700 }}>{r.advance_paid_amount != null ? `${fmtOMR(r.advance_paid_amount)} ${r.currency}` : '—'}</span>
                      </div>
                    )}
                    {r.settlement_spent != null && (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5 }}>
                          <span style={{ color: 'var(--muted)' }}>Spent</span>
                          <span style={{ fontWeight: 700 }}>{fmtOMR(r.settlement_spent)} {r.currency}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5 }}>
                          <span style={{ color: 'var(--muted)' }}>Outcome</span>
                          <span style={{ fontWeight: 700, color: r.settlement_outcome === 'return' ? '#D97706' : r.settlement_outcome === 'reimburse' ? '#2563EB' : '#16A34A' }}>
                            {r.settlement_outcome === 'return' ? `Return ${fmtOMR(Math.abs(r.settlement_difference))}` : r.settlement_outcome === 'reimburse' ? `Reimburse ${fmtOMR(r.settlement_difference)}` : 'Exact'}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Workflow actions */}
              <div style={{ background: 'var(--surface)', borderRadius: 12, border: '1px solid var(--border)', padding: 18 }}>
                <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 12 }}>Actions</div>
                {canEdit && <button onClick={() => setEditing(true)} style={{ ...btn('var(--surface2)', 'var(--text)'), width: '100%', marginBottom: 10 }}>Edit Request</button>}
                {canDelete && (
                  <div style={{ marginBottom: 10 }}>
                    <button onClick={() => setConfirmCfg({
                      title: r.status === 'draft' ? 'Delete draft?' : 'Cancel request?',
                      message: r.status === 'draft' ? 'This draft will be removed.' : 'The request will be kept in the records marked as Cancelled, and excluded from all totals.',
                      yesLabel: r.status === 'draft' ? 'Delete Draft' : 'Cancel Request', danger: true,
                      onYes: () => act(() => financeApi.delete(`/requests/${id}`).then(onBack)),
                    })} style={{ ...btn('var(--surface2)', '#DC2626'), width: '100%', marginBottom: me?.is_manager && r.status !== 'draft' ? 8 : 0 }}>{r.status === 'draft' ? 'Delete Draft' : 'Cancel Request'}</button>
                    {me?.is_manager && r.status !== 'draft' && (
                      <button onClick={() => setConfirmCfg({
                        title: 'Delete permanently?',
                        message: 'This removes the request and its entire history from the system. This cannot be undone.',
                        yesLabel: 'Delete Permanently', danger: true,
                        onYes: () => act(() => financeApi.delete(`/requests/${id}?hard=1`).then(onBack)),
                      })} style={{ ...btn('#DC2626', '#fff'), width: '100%' }}>Delete Permanently</button>
                    )}
                  </div>
                )}

                {canSubmit && missingFields && missingFields.length > 0 && (
                  <div style={{ marginBottom: 12, padding: '12px 14px', background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.35)', borderRadius: 10 }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: '#B91C1C', marginBottom: 6 }}>⚠ Can't submit yet — please complete:</div>
                    <ul style={{ margin: '0 0 10px', paddingLeft: 18, color: '#B91C1C', fontSize: 12.5, lineHeight: 1.6 }}>
                      {missingFields.map((m, i) => <li key={i}>{m}</li>)}
                    </ul>
                    <button onClick={() => { setMissingFields(null); setEditing(true); }} style={{ ...btn('#DC2626', '#fff'), padding: '6px 14px', fontSize: 12.5 }}>Edit request to fix</button>
                  </div>
                )}

                {canSubmit && (
                  <div style={{ marginTop: 4 }}>
                    <label style={lbl}>{r.request_type === 'creditcard' ? 'Choose Card Holder' : 'Choose Approver'}</label>
                    <select style={{ ...inp, marginBottom: 10 }} value={managerId} onChange={e => setManagerId(e.target.value)}>
                      <option value="">— Select —</option>
                      {users.filter(u => u.id !== r.user_id).map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                    </select>
                    {r.request_type === 'creditcard' && <div style={{ fontSize: 11, color: 'var(--muted2)', marginBottom: 10 }}>The Card Holder authorizes the card use and can see this request now and in future.</div>}
                    <button onClick={submit} disabled={busy} style={{ ...btn(), width: '100%' }}>{r.request_type === 'creditcard' ? 'Submit to Card Holder' : 'Submit for Approval'}</button>
                  </div>
                )}

                {(canManagerDecide || canFinanceDecide) && (
                  <div style={{ marginTop: 4 }}>
                    <label style={lbl}>{canFinanceDecide ? 'Finance Decision' : r.request_type === 'creditcard' ? 'Card Holder Authorization' : 'Review Decision'}</label>
                    {canFinanceDecide && twoPhase && (
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 11, color: 'var(--muted2)', marginBottom: 8 }}>
                          {r.request_type === 'iou' ? 'Approving records the advance paid; the employee then settles with receipts.' : 'Approving authorizes the card use; the employee then settles with evidence.'}
                        </div>
                        {r.request_type === 'iou' && (
                          <>
                            <label style={lbl}>Advance Amount ({r.currency})</label>
                            <input type="number" step="0.001" style={{ ...inp, marginBottom: 8 }} value={advanceAmount} onChange={e => setAdvanceAmount(e.target.value)} placeholder={fmtOMR(r.total_amount)} />
                            <label style={lbl}>Payment Reference (optional)</label>
                            <input style={{ ...inp, marginBottom: 8 }} value={advanceRef} onChange={e => setAdvanceRef(e.target.value)} />
                          </>
                        )}
                      </div>
                    )}
                    {canFinanceDecide && !twoPhase && <div style={{ fontSize: 11, color: 'var(--muted2)', marginBottom: 8 }}>Final step — any finance manager can complete this.</div>}
                    <textarea style={{ ...inp, minHeight: 64, marginBottom: 10 }} value={decisionComment} onChange={e => setDecisionComment(e.target.value)} placeholder="Optional note…" />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <button onClick={() => requireSignatureThen(() => (canFinanceDecide ? financeDecision('approve') : managerDecision('approve')))} disabled={busy} style={{ ...btn('#16A34A'), width: '100%' }}>{canFinanceDecide ? (twoPhase ? (r.request_type === 'iou' ? 'Approve & Record Advance' : 'Authorize Use') : 'Approve & Complete') : (r.request_type === 'creditcard' ? 'Authorize Card Use' : 'Approve')}</button>
                      <button onClick={() => (canFinanceDecide ? financeDecision('reject') : managerDecision('reject'))} disabled={busy} style={{ ...btn('var(--surface2)', '#DC2626'), width: '100%' }}>Return to Requester</button>
                    </div>
                  </div>
                )}

                {/* Settlement — employee submits */}
                {canApproveSettlement && (
                  <div style={{ marginTop: 4 }}>
                    <label style={lbl}>Settlement — Line Manager Approval</label>
                    <div style={{ fontSize: 11.5, color: 'var(--muted2)', marginBottom: 8 }}>
                      {r.requester_name} spent {fmtOMR(r.settlement_spent)} {r.currency}{' '}
                      ({r.settlement_outcome === 'return' ? `return ${fmtOMR(Math.abs(r.settlement_difference))}` : r.settlement_outcome === 'reimburse' ? `reimburse ${fmtOMR(r.settlement_difference)}` : 'exact'}). Review the evidence above, then approve or return.
                    </div>
                    <textarea style={{ ...inp, minHeight: 44, marginBottom: 10 }} value={decisionComment} onChange={e => setDecisionComment(e.target.value)} placeholder="Comment (optional)…" />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => requireSignatureThen(() => settlementManagerDecision('approve'))} disabled={busy} style={{ ...btn('#16A34A'), flex: 1 }}>Approve &amp; Forward to Finance</button>
                      <button onClick={() => settlementManagerDecision('reject')} disabled={busy} style={{ ...btn('var(--surface2)', '#DC2626'), flex: 1 }}>Return</button>
                    </div>
                  </div>
                )}

                {canSubmitSettlement && (
                  <div style={{ marginTop: 4 }}>
                    <label style={lbl}>Submit Settlement</label>
                    <div style={{ fontSize: 11, color: 'var(--muted2)', marginBottom: 8 }}>
                      {r.request_type === 'iou' ? `Advance: ${fmtOMR(r.advance_paid_amount != null ? r.advance_paid_amount : r.total_amount)} ${r.currency}. ` : ''}Upload your evidence above, then enter the amount actually spent.
                    </div>
                    <label style={lbl}>Amount Spent ({r.currency})</label>
                    <input type="number" step="0.001" style={{ ...inp, marginBottom: 8 }} value={spent} onChange={e => setSpent(e.target.value)} />
                    {spent !== '' && (() => {
                      const base = Number(r.request_type === 'iou' ? (r.advance_paid_amount != null ? r.advance_paid_amount : r.total_amount) : r.total_amount);
                      const diff = +(Number(spent) - base).toFixed(3);
                      const msg = diff === 0 ? 'Spent exactly — will close.' : diff < 0 ? `You will return ${fmtOMR(Math.abs(diff))} ${r.currency}.` : `Reimbursement owed to you: ${fmtOMR(diff)} ${r.currency}.`;
                      return <div style={{ fontSize: 11.5, color: diff < 0 ? '#D97706' : diff > 0 ? '#2563EB' : '#16A34A', marginBottom: 8, fontWeight: 600 }}>{msg}</div>;
                    })()}
                    <textarea style={{ ...inp, minHeight: 50, marginBottom: 10 }} value={settleNote} onChange={e => setSettleNote(e.target.value)} placeholder="Note (optional)…" />
                    {r.request_type === 'iou' && (
                      <>
                        <label style={lbl}>Choose Line Manager to approve the settlement</label>
                        <select style={{ ...inp, marginBottom: 10 }} value={settleManagerId} onChange={e => setSettleManagerId(e.target.value)}>
                          <option value="">— Select —</option>
                          {users.filter(u => u.id !== r.user_id).map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                        </select>
                      </>
                    )}
                    <button onClick={submitSettlement} disabled={busy} style={{ ...btn(), width: '100%' }}>Submit Settlement{r.request_type === 'iou' ? ' for Approval' : ''}</button>
                  </div>
                )}

                {/* Settlement — finance confirms */}
                {canFinanceConfirm && (
                  <div style={{ marginTop: 4 }}>
                    <label style={lbl}>Confirm Settlement</label>
                    <div style={{ fontSize: 11.5, color: 'var(--muted2)', marginBottom: 8 }}>
                      Spent {fmtOMR(r.settlement_spent)} {r.currency} · {r.settlement_outcome === 'return' ? `Return ${fmtOMR(Math.abs(r.settlement_difference))}` : r.settlement_outcome === 'reimburse' ? `Reimburse ${fmtOMR(r.settlement_difference)}` : 'Exact'}.
                    </div>
                    <textarea style={{ ...inp, minHeight: 50, marginBottom: 10 }} value={decisionComment} onChange={e => setDecisionComment(e.target.value)} placeholder="Optional note…" />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <button onClick={() => requireSignatureThen(() => settleFinance('approve'))} disabled={busy} style={{ ...btn('#16A34A'), width: '100%' }}>Confirm Settlement</button>
                      <button onClick={() => settleFinance('reject')} disabled={busy} style={{ ...btn('var(--surface2)', '#DC2626'), width: '100%' }}>Return to Employee</button>
                    </div>
                  </div>
                )}

                {/* Settlement — employee final confirmation */}
                {canUserConfirm && (
                  <div style={{ marginTop: 4 }}>
                    <label style={lbl}>Confirm & Close</label>
                    <div style={{ fontSize: 11.5, color: 'var(--muted2)', marginBottom: 10 }}>
                      Finance confirmed your settlement{r.settlement_outcome === 'return' ? ` — please return ${fmtOMR(Math.abs(r.settlement_difference))} ${r.currency}` : r.settlement_outcome === 'reimburse' ? ` — reimbursement of ${fmtOMR(r.settlement_difference)} ${r.currency} is due to you` : ''}. Confirm to close the request.
                    </div>
                    <button onClick={settleConfirm} disabled={busy} style={{ ...btn(), width: '100%' }}>Confirm & Close</button>
                  </div>
                )}

                {r.status === 'completed' && <div style={{ color: '#16A34A', fontSize: 13, fontWeight: 700 }}>✓ Completed — processed by {r.finance_name}.</div>}
                {['manager_rejected', 'finance_rejected'].includes(r.status) && (
                  <div style={{ color: '#DC2626', fontSize: 12.5, fontWeight: 600 }}>Returned for revision{(r.finance_comment || r.manager_comment) ? `: ${r.finance_comment || r.manager_comment}` : '.'}</div>
                )}
                {!canEdit && !canDelete && !canSubmit && !canManagerDecide && !canFinanceDecide && !canSubmitSettlement && !canApproveSettlement && !canFinanceConfirm && !canUserConfirm && !['completed', 'manager_rejected', 'finance_rejected'].includes(r.status) && (
                  <div style={{ fontSize: 12.5, color: 'var(--muted2)' }}>{inSettlement ? 'Awaiting another party in the settlement.' : 'No action available to you at this stage.'}</div>
                )}
              </div>
            </div>
          </div>
        )}

        {tab === 'history' && (
          <div style={{ background: 'var(--surface)', borderRadius: 12, border: '1px solid var(--border)', padding: '24px 28px' }}>
            <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 4 }}>Request journey</div>
            <div style={{ fontSize: 12.5, color: 'var(--muted)', marginBottom: 22 }}>Follow this request from creation to completion.</div>
            <RequestTimeline history={r.history || []} request={r} />
          </div>
        )}
        </div>
      </div>

      {editing && <RequestForm type={r.request_type} me={me} users={users} request={r} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); if (onChanged) onChanged(); }} showToast={showToast} />}
      {confirmCfg && (
        <Modal title={confirmCfg.title} onClose={() => setConfirmCfg(null)} width={420}>
          <div style={{ fontSize: 13.5, color: 'var(--text)', lineHeight: 1.5, marginBottom: 20 }}>{confirmCfg.message}</div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button onClick={() => setConfirmCfg(null)} style={btn('var(--surface2)', 'var(--text)')}>Keep</button>
            <button onClick={() => { const fn = confirmCfg.onYes; setConfirmCfg(null); fn(); }} style={btn(confirmCfg.danger ? '#DC2626' : undefined, confirmCfg.danger ? '#fff' : undefined)}>{confirmCfg.yesLabel || 'Confirm'}</button>
          </div>
        </Modal>
      )}
      {sigGate && <SignatureGateModal onClose={() => setSigGate(null)} onDone={sigGate.onDone} showToast={showToast} />}
    </div>
  );
}

// Polite window shown when an approver has no signature on file. They upload a
// PNG/JPG image which is saved to their profile (reusable across the whole
// platform), then the pending approval proceeds.
function SignatureGateModal({ onClose, onDone, showToast }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const pick = (f) => { setFile(f); setPreview(f ? URL.createObjectURL(f) : null); };
  const saveAndProceed = async () => {
    if (!file) { showToast('Please choose your signature image first.'); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('signature', file);
      await api.post('/profile/signature', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      showToast('Signature saved. Approving now.');
      onDone();
    } catch (e) { showToast(e?.response?.data?.error || e?.error || 'Could not save the signature.'); setBusy(false); }
  };
  return (
    <Modal title="Add your signature to approve" onClose={onClose} width={480}>
      <div style={{ fontSize: 13.5, color: 'var(--text)', lineHeight: 1.6, marginBottom: 16 }}>
        Before you can approve, we need your signature on file. You do not have one saved yet.
        Please upload an image of your signature (PNG or JPG). It will be saved securely to your
        profile and used automatically on this and future approvals across the platform, so you
        will only need to do this once.
      </div>
      <div style={{ border: '1px dashed var(--border)', borderRadius: 10, padding: 16, textAlign: 'center', marginBottom: 16, background: 'var(--surface2)' }}>
        {preview
          ? <img src={preview} alt="signature preview" style={{ maxHeight: 90, maxWidth: '100%', objectFit: 'contain' }} />
          : <div style={{ color: 'var(--muted)', fontSize: 13 }}>No image selected yet</div>}
      </div>
      <input type="file" accept="image/png,image/jpeg,.png,.jpg,.jpeg" onChange={e => pick(e.target.files[0])} style={{ fontSize: 13, marginBottom: 16 }} />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <button onClick={onClose} style={btn('var(--surface2)', 'var(--text)')} disabled={busy}>Cancel</button>
        <button onClick={saveAndProceed} style={btn('#16A34A', '#fff')} disabled={busy || !file}>{busy ? 'Saving…' : 'Save signature & approve'}</button>
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 12 }}>Tip: a clean image of your signature on a white or transparent background looks best on the document.</div>
    </Modal>
  );
}

function actionName(a) {
  return ({ created: 'Request Created', edited: 'Request Edited', submitted: 'Submitted for Review', manager_assigned: 'Approver Assigned', manager_approved: 'Approved by Manager', manager_rejected: 'Returned for Revision', finance_approved: 'Processed by Finance', finance_rejected: 'Returned by Finance', advance_paid: 'Advance Paid (recorded)', authorized: 'Authorized for Use', settlement_submitted: 'Settlement Submitted', settlement_finance_confirmed: 'Settlement Confirmed by Finance', settlement_user_confirmed: 'Settlement Confirmed by Employee', settlement_returned: 'Settlement Returned', attachment_uploaded: 'Attachment Added', attachment_deleted: 'Attachment Removed', pdf_generated: 'Document Generated', notification_sent: 'Notification Sent', completed: 'Request Completed', cancelled: 'Request Cancelled' }[a] || a);
}
function roleName(r) { return ({ superadmin: 'Super Admin', finance_manager: 'Finance Manager', finance_member: 'Member', none: 'No access', system: 'System' }[r] || r || ''); }

// Visual roadmap of a request's journey: a vertical line of circles, each a step
// (created, submitted, approved, finance, released, completed) with its date and the
// person who did it. Steps not yet reached are shown greyed as "pending".
function RequestTimeline({ history, request }) {
  const done = new Set(history.map(h => h.action));
  const find = (...actions) => history.find(h => actions.includes(h.action));
  const COL = { done: '#16A34A', current: '#2563EB', pending: 'var(--muted)', reject: '#DC2626' };

  // Build the ordered milestones for this request type.
  const two = request.request_type === 'iou' || request.request_type === 'creditcard';
  const steps = [];
  const push = (key, label, rec, icon) => steps.push({ key, label, rec, icon });
  push('created', 'Request created', find('created'), '📝');
  push('submitted', 'Submitted for review', find('submitted'), '📤');
  const mgr = find('manager_approved', 'authorized', 'advance_paid');
  const mgrRej = find('manager_rejected');
  push('manager', request.request_type === 'creditcard' ? 'Authorized by card holder' : 'Approved by manager', mgr || mgrRej, '✅');
  const fin = find('finance_approved', 'settlement_finance_confirmed');
  const finRej = find('finance_rejected', 'settlement_returned');
  push('finance', two ? 'Processed by finance' : 'Approved by finance', fin || finRej, '💳');
  if (two) push('settlement', 'Settlement submitted', find('settlement_submitted'), '🧾');
  push('completed', 'Completed', find('completed'), '🏁');

  const rejected = ['manager_rejected', 'finance_rejected'].includes(request.status);

  return (
    <div>
      <div style={{ position: 'relative', paddingLeft: 8 }}>
        {steps.map((s, i) => {
          const isDone = !!s.rec;
          const isReject = s.rec && /reject|returned/.test(s.rec.action);
          const color = isReject ? COL.reject : isDone ? COL.done : COL.pending;
          const last = i === steps.length - 1;
          return (
            <div key={s.key} style={{ display: 'flex', gap: 16, position: 'relative', paddingBottom: last ? 0 : 26 }}>
              {/* connector line */}
              {!last && <div style={{ position: 'absolute', left: 17, top: 34, bottom: 0, width: 2, background: isDone ? COL.done : 'var(--border)' }} />}
              {/* circle */}
              <div style={{ width: 36, height: 36, borderRadius: '50%', flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, background: isDone ? color : 'var(--surface2)', color: isDone ? '#fff' : 'var(--muted)', border: `2px solid ${isDone ? color : 'var(--border)'}`, zIndex: 1 }}>{isReject ? '✕' : s.icon}</div>
              {/* content */}
              <div style={{ flex: 1, paddingTop: 2 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: isDone ? 'var(--text)' : 'var(--muted)' }}>{s.label}</div>
                {s.rec ? (
                  <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 2 }}>
                    {fmtDMYHM(s.rec.created_at)}{s.rec.user_name ? ` · by ${s.rec.user_name}` : ''}
                    {s.rec.comment ? <div style={{ marginTop: 3, fontStyle: 'italic' }}>“{s.rec.comment}”</div> : null}
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2, opacity: 0.7 }}>Pending</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* who can approve now (eligible approvers) */}
      {request.eligible_approvers && request.eligible_approvers.length > 0 && (
        <div style={{ marginTop: 20, padding: '14px 16px', background: 'var(--surface2)', borderRadius: 10, border: '1px solid var(--border)' }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 8 }}>Awaiting action from</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {request.eligible_approvers.map((a, i) => (
              <span key={i} style={{ fontSize: 12, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, padding: '4px 12px' }}>{a.full_name || a.name}{a.role ? <span style={{ color: 'var(--muted)' }}> · {roleName(a.role)}</span> : null}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Dashboard ────────────────────────────────────────────────────────────────
function Dashboard({ me }) {
  const [d, setD] = useState(null);
  useEffect(() => { financeApi.get('/dashboard').then(setD).catch(() => setD(null)); }, []);
  if (!d) return <div style={{ color: 'var(--muted)' }}>Loading…</div>;
  const token = localStorage.getItem('flowup_token');
  const maxMonthly = Math.max(1, ...(d.monthly || []).map(m => m.c));
  const statCard = (label, value, color, sub) => (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '20px 22px' }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px', color: 'var(--muted)' }}>{label}</div>
      <div style={{ fontSize: 30, fontWeight: 850, color: color || 'var(--text)', marginTop: 8, lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--muted2)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 850 }}>{me?.is_manager ? 'Finance Dashboard' : 'My Dashboard'}</h2>
        <a href={`/api/finance-requests/register.xlsx?token=${token}`} style={{ ...btn('#16A34A'), textDecoration: 'none' }}>⬇ Export Excel Register</a>
      </div>

      {/* Stat grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 16, marginBottom: 20 }}>
        {statCard('Total Requests', d.total_requests)}
        {statCard('Pending', d.pending_requests, '#D97706')}
        {statCard('Rejected', d.rejected_requests, '#DC2626')}
        {statCard('Total Amount', fmtOMR(d.total_amount), ACCENT, 'OMR requested')}
        {statCard('Approved Amount', fmtOMR(d.approved_amount), '#16A34A', 'OMR completed')}
      </div>

      {/* Monthly chart + breakdowns */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 16, marginBottom: 16 }}>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 22 }}>
          <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 16 }}>Requests by Month</div>
          {(!d.monthly || d.monthly.length === 0) ? <div style={{ fontSize: 12, color: 'var(--muted)' }}>No data yet.</div> : (
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, height: 180 }}>
              {d.monthly.map(m => (
                <div key={m.m} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted2)' }}>{m.c}</div>
                  <div title={`${m.c} requests · ${fmtOMR(m.amt)}`} style={{ width: '70%', height: `${Math.round((m.c / maxMonthly) * 140)}px`, minHeight: 4, background: `linear-gradient(180deg, ${ACCENT}, rgba(13,148,136,0.5))`, borderRadius: '6px 6px 0 0' }} />
                  <div style={{ fontSize: 10, color: 'var(--muted)' }}>{m.m.slice(5)}/{m.m.slice(2, 4)}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 22 }}>
          <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 14 }}>By Status</div>
          {d.by_status.length === 0 ? <div style={{ fontSize: 12, color: 'var(--muted)' }}>No data.</div> :
            d.by_status.map(s => (
              <div key={s.status} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', fontSize: 13, borderBottom: '1px solid var(--border)' }}>
                <StatusPill status={s.status} /><span style={{ fontWeight: 800 }}>{s.c}</span>
              </div>
            ))}
        </div>
      </div>

      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 22 }}>
        <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 14 }}>By Type</div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {d.by_type.map(t => (
            <div key={t.request_type} style={{ flex: 1, minWidth: 220, background: 'var(--surface2)', borderRadius: 10, padding: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>{REQUEST_TYPES[t.request_type]?.label || t.request_type}</div>
              <div style={{ fontSize: 22, fontWeight: 850, color: ACCENT, marginTop: 4 }}>{t.c}</div>
              <div style={{ fontSize: 12, color: 'var(--muted2)' }}>{fmtOMR(t.amt)} OMR total</div>
            </div>
          ))}
          {d.by_type.length === 0 && <div style={{ fontSize: 12, color: 'var(--muted)' }}>No data.</div>}
        </div>
      </div>
    </div>
  );
}

// ── Settings: notifications + SMTP (finance managers) ────────────────────────
function SettingsPanel({ showToast }) {
  const [data, setData] = useState(null);
  const [notifs, setNotifs] = useState({});
  const [smtp, setSmtp] = useState({ host: '', port: '587', user: '', from: '', pass: '' });
  const [useCustom, setUseCustom] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState('');

  const load = () => financeApi.get('/settings').then(d => {
    setData(d);
    setNotifs(d.notifications || {});
    setUseCustom(d.effective?.using_custom);
    setSmtp({ host: d.smtp?.smtp_host || '', port: d.smtp?.smtp_port || '587', user: d.smtp?.smtp_user || '', from: d.smtp?.smtp_from || '', pass: '' });
  }).catch(() => setData(null));
  useEffect(() => { load(); }, []);
  if (!data) return <div style={{ padding: 20, color: 'var(--muted)' }}>Loading settings…</div>;

  const saveNotifs = async (next) => {
    setNotifs(next);
    try { await financeApi.put('/settings', { notifications: next }); } catch (e) { showToast(e.error || 'Failed'); }
  };
  const saveSmtp = async () => {
    setSaving(true);
    try {
      const payload = useCustom
        ? { smtp: { host: smtp.host, port: smtp.port, user: smtp.user, from: smtp.from || smtp.user, ...(smtp.pass ? { pass: smtp.pass } : {}) } }
        : { smtp: { host: '' } }; // empty host clears the override → revert to default
      await financeApi.put('/settings', payload);
      showToast('Email settings saved.');
      load();
    } catch (e) { showToast(e.error || 'Failed'); }
    setSaving(false);
  };
  const sendTest = async () => {
    try { await financeApi.post('/settings/test-email', { to: testTo || undefined }); showToast('Test email sent.'); }
    catch (e) { showToast(e.error || 'Could not send test email.'); }
  };

  const card = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, marginBottom: 20 };
  const eff = data.effective || {};
  return (
    <div style={{ maxWidth: 'min(92vw, 920px)' }}>
      {/* Notifications */}
      <div style={card}>
        <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 4 }}>Notifications</div>
        <div style={{ fontSize: 12, color: 'var(--muted2)', marginBottom: 16 }}>Choose which emails the system sends. Turn off any you don't want delivered.</div>
        {(data.notifyMeta || []).map(n => (
          <label key={n.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}>
            <span style={{ fontSize: 13, color: 'var(--text)' }}>{n.label}</span>
            <input type="checkbox" checked={!!notifs[n.key]} onChange={e => saveNotifs({ ...notifs, [n.key]: e.target.checked })} style={{ width: 18, height: 18, cursor: 'pointer' }} />
          </label>
        ))}
      </div>

      {/* SMTP */}
      <div style={card}>
        <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 4 }}>Email (SMTP) Settings</div>
        <div style={{ fontSize: 12, color: 'var(--muted2)', marginBottom: 14 }}>
          By default, finance emails are sent from the shared company mailbox. You can switch to your own SMTP account below.
        </div>

        <div style={{ background: 'var(--surface2)', borderRadius: 10, padding: 14, marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px', color: 'var(--muted)', marginBottom: 8 }}>Currently sending as</div>
          <div style={{ fontSize: 13, color: 'var(--text)' }}>{eff.from || eff.user || '—'}</div>
          <div style={{ fontSize: 11.5, color: 'var(--muted2)', marginTop: 4 }}>
            {eff.using_custom ? 'Using your custom SMTP account.' : 'Using the shared company mailbox (default).'} Host: {eff.host || '—'}{eff.port ? ` : ${eff.port}` : ''}
          </div>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, cursor: 'pointer' }}>
          <input type="checkbox" checked={useCustom} onChange={e => setUseCustom(e.target.checked)} style={{ width: 18, height: 18 }} />
          <span style={{ fontSize: 13, fontWeight: 600 }}>Use my own SMTP account instead of the shared mailbox</span>
        </label>

        {useCustom && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div><label style={lbl}>SMTP Host</label><input style={inp} value={smtp.host} onChange={e => setSmtp({ ...smtp, host: e.target.value })} placeholder="smtp.office365.com" /></div>
            <div><label style={lbl}>Port</label><input style={inp} value={smtp.port} onChange={e => setSmtp({ ...smtp, port: e.target.value })} placeholder="587" /></div>
            <div><label style={lbl}>Username</label><input style={inp} value={smtp.user} onChange={e => setSmtp({ ...smtp, user: e.target.value })} placeholder="finance@company.com" /></div>
            <div><label style={lbl}>From Address</label><input style={inp} value={smtp.from} onChange={e => setSmtp({ ...smtp, from: e.target.value })} placeholder="(defaults to username)" /></div>
            <div style={{ gridColumn: '1 / -1' }}><label style={lbl}>Password {data.smtp?.smtp_user ? <span style={{ textTransform: 'none', fontWeight: 400, color: 'var(--muted)' }}>(leave blank to keep current)</span> : null}</label><input type="password" style={inp} value={smtp.pass} onChange={e => setSmtp({ ...smtp, pass: e.target.value })} autoComplete="new-password" /></div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={saveSmtp} disabled={saving} style={btn()}>{saving ? 'Saving…' : 'Save Email Settings'}</button>
          <div style={{ flex: 1 }} />
          <input style={{ ...inp, width: 200 }} value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="test recipient (optional)" />
          <button onClick={sendTest} style={btn('var(--surface2)', ACCENT)}>Send Test Email</button>
        </div>
      </div>
    </div>
  );
}

// ── Admin: role management ───────────────────────────────────────────────────
function AdminPanel({ users, onChanged, showToast }) {
  const setRole = async (id, role) => {
    try { await financeApi.put(`/users/${id}/role`, { finance_role: role }); onChanged(); showToast('Role updated.'); }
    catch (e) { showToast(e.error || 'Failed'); }
  };
  return (
    <div style={{ background: 'var(--surface)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr><th style={thd}>User</th><th style={thd}>Email</th><th style={thd}>Finance Role</th></tr></thead>
        <tbody>
          {users.map(u => (
            <tr key={u.id}>
              <td style={td}>{u.full_name}{u.role === 'superadmin' && <span style={{ fontSize: 10, marginLeft: 6, color: ACCENT, fontWeight: 700 }}>SUPER</span>}</td>
              <td style={td}>{u.email}</td>
              <td style={td}>
                <select style={{ ...inp, width: 200 }} value={u.role === 'superadmin' ? 'finance_manager' : (u.finance_role || 'none')} disabled={u.role === 'superadmin'} onChange={e => setRole(u.id, e.target.value)}>
                  {FINANCE_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
